import type {
  FormattedUser,
  UpdateUserRequest,
  UserRoleOption
} from '@library/types/user';
import {
  deleteUserRolesAttributes,
  mapUserRoleOptions,
  mapUserRolesAttributes
} from '@bo-utils/user';
import { isEqualArrays, isValidArrayWithValues } from '@bo-utils/common';
import { useMemo, useState } from 'react';
import { FormActionButtons } from '@library/components/buttons';
import { UserRoleSettings } from '@bo-components/userRoles/UserRoleSettings';
import { useLeagues } from '@bo-hooks/useLeagues';
import { useRoles } from '@bo-hooks/roles/useRoles';

export interface EditUserDetailsProps {
  onClose: () => void;
  onUpdateUser: (userDetails: UpdateUserRequest) => void;
  onViewPermissions: () => void;
  user: FormattedUser;
}

export const EditUserDetails = ({
  onClose,
  onUpdateUser,
  onViewPermissions,
  user
}: EditUserDetailsProps) => {
  const [editUserRoles, setEditUserRoles] = useState<Array<UserRoleOption>>([]);
  const [isSaveDisabled, setIsSaveDisabled] = useState(false);

  const { leagueOptions } = useLeagues();
  const { roleComboboxOptions: roleOptions, rolesWithPermissions } = useRoles(
    {}
  );

  const userRoleOptions = useMemo(
    () => mapUserRoleOptions(user, leagueOptions),
    [leagueOptions, user]
  );

  const saveDisabled = useMemo(
    () =>
      editUserRoles?.length === 0 ||
      isEqualArrays(userRoleOptions, editUserRoles, 'role.value') ||
      !isValidArrayWithValues(editUserRoles, 'role') ||
      isSaveDisabled,
    [editUserRoles, isSaveDisabled, userRoleOptions]
  );

  const handleUpdateUser = () => {
    setIsSaveDisabled(true);
    const validRoles = isValidArrayWithValues(editUserRoles);

    if (validRoles && editUserRoles?.length) {
      // For PATCH admin_users, delete edited user_role before updating it's role settings
      const deletedUserRoles = deleteUserRolesAttributes(
        editUserRoles,
        user.roles
      );

      const updatedUserRoles = mapUserRolesAttributes(editUserRoles);

      const userRoleAttributes = [...deletedUserRoles, ...updatedUserRoles];

      const updateReq = {
        admin_user: {
          user_roles_attributes: userRoleAttributes
        }
      };
      onUpdateUser(updateReq);
    }
  };

  return (
    <>
      <UserRoleSettings
        leagueOptions={leagueOptions}
        roleOptions={roleOptions}
        rolesWithPermissions={rolesWithPermissions}
        userRoleOptions={userRoleOptions}
        onEditUserRoles={setEditUserRoles}
        onViewPermissions={onViewPermissions}
      />
      <FormActionButtons
        primary={{ onClick: handleUpdateUser, disabled: saveDisabled }}
        secondary={{ onClick: onClose }}
      />
    </>
  );
};
